import { Select } from '@/components/common/Select';
import { DateField } from '@/components/forms/DateField';
import type { WaterSourceMetadata } from '@/types/waypoint';

interface WaterSourceFormProps {
  metadata: Partial<WaterSourceMetadata>;
  onChange: (updated: Partial<WaterSourceMetadata>) => void;
}

const WATER_TYPE_OPTIONS = [
  { value: 'pond', label: 'Pond' },
  { value: 'creek', label: 'Creek' },
  { value: 'spring', label: 'Spring' },
  { value: 'waterhole_constructed', label: 'Constructed Waterhole' },
  { value: 'other', label: 'Other' },
];

interface ToggleProps {
  label: string;
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function Toggle({ label, id, checked, onChange }: ToggleProps) {
  return (
    <label htmlFor={id} className="flex items-center justify-between py-2 cursor-pointer">
      <span className="field-label">
        {label}
      </span>
      <button
        id={id}
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border)]'
        }`}
        role="switch"
        aria-checked={checked}
        data-testid="seasonal-toggle"
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </label>
  );
}

export function WaterSourceForm({ metadata, onChange }: WaterSourceFormProps) {
  return (
    <div className="flex flex-col gap-4">
      <Select
        id="water-type"
        label="Water Type"
        value={metadata.waterType ?? 'pond'}
        onChange={(v) =>
          onChange({ ...metadata, waterType: v as WaterSourceMetadata['waterType'] })
        }
        options={WATER_TYPE_OPTIONS}
      />

      <Toggle
        id="water-seasonal"
        label="Dries Up Seasonally?"
        checked={metadata.seasonal ?? false}
        onChange={(v) => onChange({ ...metadata, seasonal: v })}
      />

      <DateField
        id="water-last-checked"
        label="Last Checked Date"
        value={metadata.lastCheckedDate ?? ''}
        onChange={(v) => onChange({ ...metadata, lastCheckedDate: v || undefined })}
      />
    </div>
  );
}
